import { Box, Button, Group, Loader, Text, ThemeIcon } from '@mantine/core';
import { IconCheck, IconSignature } from '@tabler/icons-react';
import type { AutosaveStatus } from '../hooks/useProviderNoteAutosave';

export interface ProgressNoteStickyFooterProps {
  autosaveStatus: AutosaveStatus;
  autosaveSavedAt?: Date | null;
  /** Addendum only — discard/cancel without saving. Hidden for draft notes (stay open until Review & Sign). */
  onCancel?: () => void;
  cancelLabel?: string;
  onReviewAndSign?: () => void;
  onReviewAndClose?: () => void;
  showSignAction?: boolean;
  showFinishAction?: boolean;
  /** Manual save flow for signed-note addendums (no autosave indicator). */
  variant?: 'draft' | 'addendum';
  saveLabel?: string;
  loading?: boolean;
  closingEditor?: boolean;
  disabled?: boolean;
}

function AutosaveStatusIndicator({
  autosaveStatus,
  autosaveSavedAt,
  closingEditor = false,
}: Pick<ProgressNoteStickyFooterProps, 'autosaveStatus' | 'autosaveSavedAt' | 'closingEditor'>): JSX.Element {
  const isSaving =
    autosaveStatus === 'saving' || (closingEditor && autosaveStatus !== 'saved');

  if (isSaving) {
    return (
      <Group gap={6} wrap="nowrap" aria-live="polite">
        <Loader size="xs" color="gray" />
        <Text size="xs" c="gray.6">
          {closingEditor ? 'Saving before closing…' : 'Saving…'}
        </Text>
      </Group>
    );
  }

  if (autosaveStatus === 'saved') {
    const savedTime = autosaveSavedAt
      ? autosaveSavedAt.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })
      : null;

    return (
      <Group gap={6} wrap="nowrap" aria-live="polite">
        <ThemeIcon size="sm" color="green" variant="light" radius="xl" aria-hidden>
          <IconCheck size={14} />
        </ThemeIcon>
        <Text size="xs" c="gray.7">
          Saved{savedTime ? ` ${savedTime}` : ''}
        </Text>
      </Group>
    );
  }

  if (autosaveStatus === 'unsaved') {
    return (
      <Text size="xs" c="gray.6" aria-live="polite">
        Unsaved changes
      </Text>
    );
  }

  return (
    <Text size="xs" c="red.7" aria-live="polite">
      Unable to save
    </Text>
  );
}

export function ProgressNoteStickyFooter({
  autosaveStatus,
  autosaveSavedAt,
  onCancel,
  cancelLabel = 'Cancel',
  onReviewAndSign,
  onReviewAndClose,
  showSignAction,
  showFinishAction,
  variant = 'draft',
  saveLabel,
  loading = false,
  closingEditor = false,
  disabled = false,
}: ProgressNoteStickyFooterProps): JSX.Element {
  const isAddendum = variant === 'addendum';
  const showCancel = isAddendum && Boolean(onCancel);
  const primarySaveLabel = saveLabel ?? (isAddendum ? 'Save' : 'Review & Close');

  return (
    <Box
      component="footer"
      px="md"
      py="sm"
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        {isAddendum ? (
          <Text size="xs" c="gray.6" aria-live="polite">
            Review quality checks, add a revision reason, then re-sign. Changes are not autosaved.
          </Text>
        ) : (
          <AutosaveStatusIndicator
            autosaveStatus={autosaveStatus}
            autosaveSavedAt={autosaveSavedAt}
            closingEditor={closingEditor}
          />
        )}
        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          {showCancel ? (
            <Button variant="default" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
          ) : null}
          {showFinishAction && onReviewAndClose ? (
            <Button color="gold" onClick={onReviewAndClose} loading={loading} disabled={disabled}>
              {primarySaveLabel}
            </Button>
          ) : null}
          {showSignAction && onReviewAndSign ? (
            <Button
              color="gold"
              leftSection={<IconSignature size={16} />}
              onClick={onReviewAndSign}
              loading={loading}
              disabled={disabled}
            >
              Review & Sign
            </Button>
          ) : null}
        </Group>
      </Group>
    </Box>
  );
}
